import { useEffect, useRef } from "react";

import { PaginationBar } from "@/components/common/PaginationBar";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import { customerDisplayName, formatInboundDate } from "./ideaRoomCustomerInboxModel";
import { IdeaRoomCustomerThreadCard } from "./IdeaRoomCustomerThreadCard";
import type { IntakeTab } from "./useOrderIntakeReview";
import { useIdeaRoomCustomerInbox } from "./useIdeaRoomCustomerInbox";

export function IdeaRoomCustomerInbox({
  activeTab,
  onOpenIntake,
  onInitialAvailability,
}: {
  activeTab: IntakeTab;
  onOpenIntake: (intake: OpsOrderIntakeRow) => void;
  onInitialAvailability?: (available: boolean) => void;
}) {
  const inbox = useIdeaRoomCustomerInbox(activeTab, true);
  const reportedAvailability = useRef(false);
  useEffect(() => {
    if (!inbox.loaded || reportedAvailability.current) return;
    reportedAvailability.current = true;
    onInitialAvailability?.(inbox.page.total > 0);
  }, [inbox.loaded, inbox.page.total, onInitialAvailability]);
  return (
    <div className="space-y-3">
      <SectionCard title="IdeaRoom customers">
        <div className="space-y-3">
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              inbox.submitSearch();
            }}
          >
            <input
              value={inbox.searchDraft}
              onChange={(event) => inbox.setSearchDraft(event.target.value)}
              placeholder="Search name, email, or phone"
              className="min-w-56 flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            />
            <input
              value={inbox.eventType}
              onChange={(event) => inbox.setEventType(event.target.value.trim())}
              placeholder="Event type"
              className="w-36 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            />
            <button type="submit" className={ghostBtnClass}>Search</button>
          </form>
          <div className="flex flex-wrap gap-4 text-xs text-gray-700 dark:text-zinc-300">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={inbox.unresolvedIdentity}
                onChange={(event) => inbox.setUnresolvedIdentity(event.target.checked)}
              />
              Identity conflicts
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={inbox.unresolvedLocation}
                onChange={(event) => inbox.setUnresolvedLocation(event.target.checked)}
              />
              Unresolved locations
            </label>
          </div>
        </div>
      </SectionCard>

      {inbox.error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {inbox.error}
        </div>
      ) : null}

      {inbox.loading ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading customers…</div>
      ) : inbox.profiles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
          No IdeaRoom customers match this queue and filter.
        </div>
      ) : (
        <div className="space-y-2">
          {inbox.profiles.map((profile) => {
            const selected = inbox.selectedProfileId === profile.id;
            return (
              <div key={profile.id} className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                <button
                  type="button"
                  className="w-full px-4 py-3 text-left"
                  onClick={() => inbox.toggleProfile(profile.id)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-zinc-100">
                        {customerDisplayName(profile)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-zinc-400">
                        {[profile.email, profile.phone].filter(Boolean).join(" · ") || "No contact identity"}
                      </div>
                    </div>
                    <div className="text-right text-xs text-gray-500 dark:text-zinc-400">
                      <div>{formatInboundDate(profile.latest_activity_at)}</div>
                      <div>{profile.thread_count} pushes · {profile.pending_count} pending</div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {profile.identity_conflict ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-950 dark:text-red-200">Identity conflict</span> : null}
                    {profile.unresolved_location ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-200">Location unresolved</span> : null}
                    {profile.correlation_statuses.map((status) => (
                      <span key={status} className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200">
                        {status === "awaiting_quote" ? "Standalone" : status === "quote_matched" ? "Quote matched" : status === "order_matched" ? "Order matched" : "Match review"}
                      </span>
                    ))}
                    {profile.latest_event_types.map((type) => <span key={type} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-zinc-800 dark:text-zinc-300">{type}</span>)}
                  </div>
                </button>
                {selected ? (
                  <div className="border-t border-gray-200 p-3 dark:border-zinc-700">
                    {inbox.detailLoading || !inbox.detail ? (
                      <div className="text-xs text-gray-500">Loading order history…</div>
                    ) : (
                      <div className="space-y-3">
                        {inbox.detail.threads.map((thread) => (
                          <IdeaRoomCustomerThreadCard
                            key={thread.id}
                            thread={thread}
                            activeLocations={inbox.locations}
                            mappedLocations={inbox.detail!.locations}
                            confirming={inbox.confirming}
                            assetActionId={inbox.assetActionId}
                            onConfirm={inbox.confirmLocation}
                            onAssetAction={inbox.actOnAsset}
                            onOpenIntake={onOpenIntake}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <PaginationBar
        pageIndex={inbox.page.pageIndex}
        pageCount={inbox.page.pageCount}
        pageSize={inbox.page.pageSize}
        totalCount={inbox.page.total}
        firstShown={inbox.page.firstShown}
        lastShown={inbox.page.lastShown}
        onPageChange={inbox.setPageIndex}
        onPageSizeChange={() => undefined}
        pageSizeOptions={[10]}
        noun="customers"
        alwaysShow
      />
    </div>
  );
}
